import { UploadOutlined } from '@ant-design/icons'
import { Upload } from 'antd'
import type { UploadChangeParam, UploadFile } from 'antd/es/upload'
import { validateFile } from 'features/files/utils/fileUploadUtils'
import { type FC, useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const { Dragger } = Upload

interface FileUploadDraggerProps {
  uploading?: boolean
  fileList: ReadonlyArray<UploadFile>
  onFileListChange: (fileList: ReadonlyArray<UploadFile>) => void
  maxFileSize?: number
  acceptedFileTypes?: ReadonlyArray<string>
  multiple?: boolean
  directory?: boolean
  disabled?: boolean
  className?: string
  condensed?: boolean
  showUploadList?: boolean
  useCuiBackground?: boolean
}

const UploadIcon = styled.div<{ $condensed: boolean }>`
  font-size: 3rem;
  color: ${CantileverTheme.colors.grayscale[500]};
  margin-bottom: ${({ $condensed }) => ($condensed ? '0rem' : '1rem')};
`

const UploadText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${CantileverTheme.colors.grayscale[800]};
  margin-bottom: 0.5rem;
`

const UploadHint = styled.div`
  font-size: 0.875rem;
  color: ${CantileverTheme.colors.grayscale[500]};
`

const cuiUploadStyles = css`
  .ant-upload-drag {
    border-color: #7c5bb2;
    background: #502b85;

    &:hover,
    &.ant-upload-drag-hover {
      border-color: #a98de0;
      background: #5f349d;
    }
  }

  ${UploadIcon},
  ${UploadText} {
    color: #ffffff;
  }

  ${UploadHint} {
    color: #ede7f6;
  }
`

const StyledDragger = styled(Dragger)<{
  $condensed: boolean
  $useCuiBackground: boolean
}>`
  .ant-upload-drag {
    border: 0.125rem dashed ${CantileverTheme.colors.gray['600']};
    border-radius: 0.5rem;
    background: ${CantileverTheme.colors.gray['600']};
    padding: ${({ $condensed }) => ($condensed ? '1rem' : '2rem 1rem')};
    text-align: center;
    transition: all 0.3s ease;
    cursor: pointer;
    min-height: ${({ $condensed }) => ($condensed ? '80px' : '150px')};

    &:hover {
      border-color: ${CantileverTheme.colors.grayscale[400]};
      background: ${CantileverTheme.colors.grayscale[200]};
    }

    &.ant-upload-drag-hover {
      border-color: ${CantileverTheme.colors.grayscale[400]};
      background: ${CantileverTheme.colors.grayscale[200]};
    }
  }

  .ant-upload-drag-container {
    padding: 0;
    min-height: inherit;
  }

  .ant-upload {
    padding: ${({ $condensed }) => ($condensed ? '8px' : '16px')} !important;
  }

  .ant-upload-list-item .ant-upload-icon {
    flex-shrink: 0;
  }

  ${({ $useCuiBackground }) => $useCuiBackground && cuiUploadStyles}
`

/**
 * A dragger component for file uploads that handles file selection and validation
 */
export const FileUploadDragger: FC<FileUploadDraggerProps> = ({
  uploading = false,
  fileList,
  onFileListChange,
  maxFileSize = 20,
  acceptedFileTypes = ['*/*'],
  multiple = true,
  directory = false,
  disabled,
  condensed = false,
  showUploadList = true,
  useCuiBackground = false,
}) => {
  const handleChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      const sortedFiles = [...info.fileList].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
      onFileListChange(sortedFiles)
    },
    [onFileListChange]
  )

  const beforeUpload = useCallback(
    (file: File) => {
      // if we upload a folder from Mac, it includes .DS_Store files
      if (file.name === '.DS_Store') {
        return Upload.LIST_IGNORE
      }
      return validateFile(file, maxFileSize, acceptedFileTypes)
    },
    [maxFileSize, acceptedFileTypes]
  )

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      multiple,
      directory,
      fileList,
      showUploadList,
      beforeUpload,
      onChange: handleChange,
      disabled,
      onRemove: (file: UploadFile) => {
        const newFileList = fileList.filter((f) => f.uid !== file.uid)
        onFileListChange(newFileList)
        return true
      },
    }),
    [
      multiple,
      directory,
      fileList,
      beforeUpload,
      handleChange,
      disabled,
      onFileListChange,
      showUploadList,
    ]
  )

  return (
    <StyledDragger
      {...uploadProps}
      fileList={[...fileList]}
      $condensed={condensed}
      $useCuiBackground={useCuiBackground}>
      {!uploading && (
        <>
          <UploadIcon $condensed={condensed}>
            <UploadOutlined style={{ fontSize: condensed ? '2rem' : '3rem' }} />
          </UploadIcon>
          {!condensed ? (
            <UploadText>
              {directory
                ? 'Click or drag files or folders to this area to upload'
                : 'Click or drag files to this area to upload'}
            </UploadText>
          ) : (
            <UploadText>Upload Files</UploadText>
          )}
          {!condensed && (
            <UploadHint>
              {acceptedFileTypes.includes('*/*')
                ? `Supports all file types. Max ${maxFileSize}MB per file.`
                : `Accepted types: ${acceptedFileTypes.join(', ')}. Max ${maxFileSize}MB per file.`}
            </UploadHint>
          )}
        </>
      )}
    </StyledDragger>
  )
}
